import React, { useState } from 'react';
import { Box, Typography, Chip, Paper, IconButton, Tooltip, Avatar } from '@mui/material';
import { Edit, Add, Block, CheckCircle } from '@mui/icons-material';
import { DataTable } from '@shared/components/DataTable/DataTable';
import { PageHeader } from '@shared/components/PageHeader';
import { LoadingSpinner, ConfirmationDialog } from '@shared/components';
import { useUsers } from '../hooks/useUsers';
import { useRoles } from '../hooks/useRoles';
import { EditUserDialog } from '../components/EditUserDialog';
import type { User } from '../types';
import { USER_STATUS } from '../types';
import { format } from 'date-fns';

const UsersList: React.FC = () => {
  const { users, isLoading, createUser, updateUser, blockUser, isCreating, isUpdating } = useUsers();
  const { roles, isLoading: rolesLoading } = useRoles();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [userToBlock, setUserToBlock] = useState<User | null>(null);

  const handleAddClick = () => {
    setSelectedUser(null);
    setDialogOpen(true);
  };

  const handleEditClick = (user: User) => {
    setSelectedUser(user);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedUser(null);
  };

  const handleCreateUser = async (data: { email: string; name: string; phone: string; role_id: number; game_id: string }) => {
    await createUser(data);
  };

  const handleUpdateUser = async (data: { name: string; phone: string; role_id: number; game_id: string }) => {
    if (selectedUser) {
      await updateUser({ userId: selectedUser.user_id, data: { ...data, phone: data.phone || null } });
    }
  };

  const handleBlockClick = (user: User) => {
    setUserToBlock(user);
    setConfirmDialogOpen(true);
  };

  const handleConfirmBlock = async () => {
    if (userToBlock) {
      const newStatus = userToBlock.status === USER_STATUS.BLOCKED ? USER_STATUS.UNVERIFIED : USER_STATUS.BLOCKED;
      await blockUser({
        userId: userToBlock.user_id,
        data: {
          name: userToBlock.name,
          phone: userToBlock.phone,
          role_id: userToBlock.role_id,
          status: newStatus,
        },
      });
      setConfirmDialogOpen(false);
      setUserToBlock(null);
    }
  };

  const handleCancelBlock = () => {
    setConfirmDialogOpen(false);
    setUserToBlock(null);
  };

  const columns = [
    {
      id: 'name',
      label: 'User',
      minWidth: 220,
      format: (value: string, row: User) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Avatar src={row.profile_image || undefined} sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
            {value.charAt(0).toUpperCase()}
          </Avatar>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {value}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {row.email}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      id: 'phone',
      label: 'Phone',
      minWidth: 150,
      format: (value: string | null) => <Typography variant="body2">{value || '-'}</Typography>,
    },
    {
      id: 'role_name',
      label: 'Role',
      minWidth: 120,
      format: (value: string) => <Chip label={value} color="primary" size="small" variant="outlined" />,
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 100,
      align: 'center' as const,
      format: (value: string) => {
        let color: 'success' | 'error' | 'warning' | 'default' = 'default';
        if (value === USER_STATUS.VERIFIED) color = 'success';
        else if (value === USER_STATUS.BLOCKED) color = 'error';
        else if (value === USER_STATUS.UNVERIFIED) color = 'warning';

        return <Chip label={value} color={color} size="small" />;
      },
    },
    {
      id: 'last_login_at',
      label: 'Last Login',
      minWidth: 160,
      format: (value: string) => (
        <Typography variant="body2">{value ? format(new Date(value), 'MMM dd, yyyy HH:mm') : '-'}</Typography>
      ),
    },
    {
      id: 'actions',
      label: 'Actions',
      minWidth: 150,
      align: 'center' as const,
      sortable: false,
      format: (value: any, row: User) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
          <Tooltip title="Edit User">
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleEditClick(row)}
              sx={{
                '&:hover': {
                  backgroundColor: 'primary.lighter',
                },
              }}
            >
              <Edit />
            </IconButton>
          </Tooltip>
          <Tooltip title={row.status === USER_STATUS.BLOCKED ? 'Unblock User' : 'Block User'}>
            <IconButton
              size="small"
              color={row.status === USER_STATUS.BLOCKED ? 'success' : 'error'}
              onClick={() => handleBlockClick(row)}
              sx={{
                '&:hover': {
                  backgroundColor: row.status === USER_STATUS.BLOCKED ? 'success.lighter' : 'error.lighter',
                },
              }}
            >
              {row.status === USER_STATUS.BLOCKED ? <CheckCircle /> : <Block />}
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  if (isLoading || rolesLoading) {
    return <LoadingSpinner message="Loading users..." fullHeight />;
  }

  return (
    <Box sx={{ px: 1, pt: 1 }}>
      <PageHeader
        title="User Management"
        subtitle="Manage user accounts and roles"
        // primaryActionLabel="Add User"
        onPrimaryAction={handleAddClick}
        primaryIcon={<Add />}
      />

      <Paper variant="outlined" sx={{ borderRadius: 2, mt: 2 }}>
        <DataTable
          columns={columns}
          data={users}
          loading={isLoading}
          page={0}
          rowsPerPage={users.length}
          totalRows={users.length}
          onPageChange={() => { }}
          onRowsPerPageChange={() => { }}
          onSort={() => { }}
          orderBy=""
          order="asc"
          emptyMessage="No users found"
          columnVisibilityKey="users_table"
        />
      </Paper>

      <EditUserDialog
        open={dialogOpen}
        user={selectedUser}
        roles={roles}
        onClose={handleCloseDialog}
        onSave={handleUpdateUser}
        onCreate={handleCreateUser}
        isSaving={isCreating || isUpdating}
      />

      <ConfirmationDialog
        open={confirmDialogOpen}
        onClose={handleCancelBlock}
        onConfirm={handleConfirmBlock}
        title={userToBlock?.status === USER_STATUS.BLOCKED ? 'Unblock User' : 'Block User'}
        message={
          userToBlock?.status === USER_STATUS.BLOCKED
            ? `Are you sure you want to unblock ${userToBlock?.name}? They will regain access to the system.`
            : `Are you sure you want to block ${userToBlock?.name}? They will lose access to the system.`
        }
        confirmText={userToBlock?.status === USER_STATUS.BLOCKED ? 'Unblock' : 'Block'}
        cancelText="Cancel"
        variant={userToBlock?.status === USER_STATUS.BLOCKED ? 'info' : 'error'}
        confirmColor={userToBlock?.status === USER_STATUS.BLOCKED ? 'success' : 'error'}
      />
    </Box>
  );
};

export default UsersList;
